let swiperRegistered = false;

/**
 * Registra os custom elements do Swiper sob demanda (Lever 1).
 *
 * Antes, `registerSwiperElements()` rodava no main.ts para 100% das visitas,
 * pondo ~370KB de Swiper no vendor eager e executando os define() no boot —
 * mas só as telas com carrossel de destaques (header) usam
 * <swiper-container>. Aqui o bundle é import()-ado só quando essas telas de
 * fato têm imagens de destaque. Custom elements fazem upgrade retroativo, então
 * registrar depois de o <swiper-container> já estar no DOM funciona.
 *
 * ── Por que existe o `catch` (10/09/2026) ───────────────────────────────────
 *
 * O chamador (`header.page.ts`) NÃO espera esta Promise: `if (…length) {
 * ensureSwiperRegistered(); }`. Sem `catch`, uma rejeição vira unhandled
 * rejection, sobe ao ErrorHandler global como `ChunkLoadError` e o
 * `ChunkLoadRecovery` RECARREGA O CARDÁPIO INTEIRO — por causa de um carrossel
 * decorativo.
 *
 * E o cardápio recarregava exatamente para quem menos podia pagar por isso.
 * Medido no Athena em 06–10/09, nos erros deste chunk: `chunk_type='error'` com
 * `res_transfer_size = 0` e `res_ms` de **9,8 s, 22 s, 30 s, 49 s, 50 s, 54 s**,
 * e um `timeout` de **189 s** em `net_type='3g'`. Não é bloqueador de conteúdo
 * nem MIME: é conexão lenta que estolou. Recarregar a página nesse cenário é a
 * pior resposta possível — troca um carrossel que faltou por um boot inteiro
 * numa rede que já não estava dando conta.
 *
 * Sem o Swiper o `<swiper-container>` fica um elemento desconhecido e as
 * imagens empilham em vez de deslizar. A tela continua de pé e o cliente
 * continua pedindo.
 *
 * O flag volta a `false` no erro DE PROPÓSITO: ele é a trava de "já registrei",
 * e mantê-lo `true` depois de uma falha condenaria a sessão a nunca mais tentar
 * — inclusive depois de a rede voltar. Como a causa medida é transitória, a
 * próxima tela que precise do carrossel merece uma tentativa nova. Isso não
 * vira laço: cada tentativa depende de uma nova montagem de tela com imagens de
 * destaque, não de um timer.
 *
 * **Como se prova que funcionou:** `ChunkLoadError` com `chunk_url` contendo
 * `node_modules_swiper_swiper-element-bundle_mjs` vai a ZERO (eram 5 devices em
 * 06–10/09). O controle negativo que NÃO pode se mover no mesmo período é o
 * chunk `default-src_app_shared_utils_leaflet-map-lifecycle_ts` (19 devices),
 * que é dependência de rota e continua sem `catch` possível — se ele cair
 * junto, a queda foi de tráfego, não desta correção.
 */
export async function ensureSwiperRegistered(
	carregar: () => Promise<{ register: () => void }> = () => import('swiper/element/bundle'),
): Promise<void> {
	if (swiperRegistered) return;
	swiperRegistered = true;
	try {
		const { register } = await carregar();
		register();
	} catch (e) {
		swiperRegistered = false;
	}
}

/** Só para teste: devolve o módulo ao estado de antes do primeiro registro. */
export function _resetarSwiperParaTeste(): void {
	swiperRegistered = false;
}
